import { dotClassOf, statusLabelOf } from '../lib/appearance';
import { endOf, startOf, titleOf } from '../lib/adapters';
import { Button } from '@/shared/components/ui/button';
import { Sheet } from '@/shared/components/ui/sheet';
import { formatDateTimeRange } from '@/shared/lib/dates';
import { formatList } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import type { CalendarItem } from '@/shared/types/domain';

interface EventSheetProps {
  /** `null` = fechada. O item fica montado durante a saída, por conta da `Sheet`. */
  item: CalendarItem | null;
  onClose: () => void;
  onShow: (item: CalendarItem) => void;
}

/**
 * **Clicar num chip não navega — abre esta folha** (spec-calendar-ux.md §7,
 * decisões 84 e 85).
 *
 * O motivo é o tamanho do alvo: um chip da vista de Mês tem 2mm de altura, e
 * clicar nele é metade intenção e metade pontaria. Navegar direto dali cobra uma
 * ida e uma volta de quem só queria confirmar de que projeto era aquela faixa.
 * A folha responde a pergunta no lugar, e **"Mostrar" é quem navega**.
 *
 * ⚠️ **Ela não busca NADA.** O conteúdo é o `CalendarItem` que a grade já tem em
 * memória. A lista de colunas da RPC é a decisão de privacidade (ADR-015 §5), e
 * uma folha que buscasse "só mais um campo" a contornaria pela porta dos fundos
 * — sem responsável, sem orçamento, sem descrição, e isso é o teto, não o que
 * coube.
 *
 * **Ministério e coordenação são texto, não link:** a folha tem um destino só, e
 * três saídas fariam a pessoa escolher antes de saber o que está escolhendo.
 *
 * **Por que folha e não popover ancorado ao chip** (regra 13): popover ancorado é
 * máquina nova — medir, virar perto da borda, seguir o scroll — por precisão
 * espacial que na coluna estreita e no celular se perde de qualquer jeito. A
 * `Sheet` já existe, com foco preso, `Esc` e devolução de foco resolvidos.
 */
export function EventSheet({ item, onClose, onShow }: EventSheetProps) {
  const start = item ? startOf(item) : null;
  const end = item ? endOf(item) : null;
  const spaces =
    item?.kind === 'project'
      ? item.project.space_names
      : (item?.occurrence.space_names ?? []);

  return (
    <Sheet
      open={item !== null}
      onClose={onClose}
      title={item ? titleOf(item) : ''}
      placement="bottom"
      /* **Ramo inerte** (decisão 85): item que não é projeto abre a folha SEM
         "Mostrar", porque não existe tela de detalhe para a rotina da igreja
         (spec-fixed-agenda.md §7). Hoje isso nunca acontece — a agenda fixa não
         está implementada (§3.3) —, e o ramo existe para que o dia em que ela
         nascer não comece com um clique morto. Sem "Mostrar" não sobra rodapé:
         sair já é o "X" do topo, o `Esc` e o arrasto. */
      footer={
        item?.kind === 'project' ? (
          <div className="flex justify-end">
            <Button onClick={() => onShow(item)}>Mostrar</Button>
          </div>
        ) : undefined
      }
    >
      {item ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <span
                aria-hidden="true"
                className={cn('size-2 shrink-0 rounded-full', dotClassOf(item))}
              />
              {/* A situação por extenso, ao lado da cor que a grade usou — é
                  aqui que os dois canais se encontram e um explica o outro. */}
              {statusLabelOf(item)}
            </span>
            {item.kind === 'project' ? (
              <span className="text-muted-foreground">
                {item.project.ministry_name} · {item.project.coordination_name}
              </span>
            ) : null}
          </div>

          <dl className="space-y-3 text-sm">
            <Row label="Quando">
              {start && end ? formatDateTimeRange(start, end) : start ? formatDateTimeRange(start, start) : '—'}
            </Row>
            <Row label="Onde">{spaces.length > 0 ? formatList(spaces) : '—'}</Row>
          </dl>
        </div>
      ) : null}
    </Sheet>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <dt className="w-20 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}
